/**
 * i18n configuration
 * ============================================================================
 *
 * react-i18next setup with:
 *   - Auto-detection of browser language (falls back to English)
 *   - Persistence in localStorage so the choice sticks across sessions
 *   - Currently supported: English (en), German (de)
 *   - Adding a new language: drop a translations file at src/i18n/locales/<code>.json
 *     and add the code to SUPPORTED_LANGUAGES below
 *
 * Architectural note for the CTO:
 * ------------------------------------------------------------
 * For a production multi-tenant SaaS, you'll want:
 *   1. Server-side language negotiation via Accept-Language headers
 *   2. Per-tenant default language (a German company should default to DE
 *      regardless of what browser their employees use)
 *   3. Translation memory tooling (Lokalise, Crowdin, Phrase) once you go
 *      beyond two languages
 *   4. CDN caching of translation files
 * For the demo / pre-seed phase, the client-side setup below is fine.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import es from './locales/es.json';
import it from './locales/it.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', nativeLabel: 'English',  flag: '🇬🇧' },
  { code: 'de', label: 'German',  nativeLabel: 'Deutsch',  flag: '🇩🇪' },
  { code: 'fr', label: 'French',  nativeLabel: 'Français', flag: '🇫🇷' },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español',  flag: '🇪🇸' },
  { code: 'it', label: 'Italian', nativeLabel: 'Italiano', flag: '🇮🇹' },
];

export const DEFAULT_LANGUAGE = 'en';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      de: { translation: de },
      fr: { translation: fr },
      es: { translation: es },
      it: { translation: it },
    },
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'clearequal.lang',
    },
    react: {
      useSuspense: false, // Don't suspend on initial load
    },
  });

export default i18n;
